import { default as cx } from 'classnames';
import { getDeltaTextClassNames } from '../utils/getDeltaTextClassNames';

interface TextScoreProps {
  /** The main score value to display prominently */
  score?: number | string;
  /** Format of the score value, either 'percentage' or 'absolute'. If 'percentage', adds % after score. */
  scoreFormat?: 'percentage' | 'absolute';
  /** The total/maximum value */
  total?: number | string;
  /** Optional suffix to append after the total (e.g., "queries", "points") */
  suffix?: string | React.ReactNode;
  /** Show skeleton loaders instead of values */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Hide total (including when loading) if not used */
  hideTotal?: boolean;
  /** The change in score since the last measurement */
  delta?: number;
  /** Format of the delta value, either 'percentage' or 'absolute' to format as +2 vs +2% */
  deltaFormat?: 'percentage' | 'absolute';
  /** Optional suffix for the delta value (e.g., "since last scan") */
  deltaSuffix?: string | React.ReactNode;
}

/**
 * TextScore component displays a score over a total in a formatted way.
 * The score is displayed larger and bolder than the total.
 *
 * @example
 * <TextScore score={45} total={100} /> // renders "45 / 100"
 * <TextScore score={23} total={50} suffix="queries" /> // renders "23 / 50 queries"
 * <TextScore score={23} total={50} suffix="queries" isLoading={true} /> // renders skeleton loaders
 */
export default function TextScore({
  score,
  total,
  suffix,
  isLoading = false,
  className,
  hideTotal = false,
  delta,
  deltaFormat = 'absolute',
  deltaSuffix,
  scoreFormat = 'absolute',
}: TextScoreProps) {
  return (
    <div className={cx(className, 'yst-font-medium')}>
      <span className="yst-mr-1 yst-text-xl yst-font-bold yst-text-slate-900">
        {isLoading || score === undefined || score === null ? '—' : score}
        {!isLoading &&
        score !== undefined &&
        score !== null &&
        scoreFormat === 'percentage'
          ? '%'
          : ''}
      </span>
      {!hideTotal && total && total !== 0 && total !== '0' && (
        <span className="yst-text-slate-500">/ {isLoading ? '—' : total}</span>
      )}
      {suffix && <span className="yst-text-slate-500"> {suffix}</span>}
      {typeof delta === 'number' && !isNaN(delta) && (
        <span className={cx('yst-ml-2', getDeltaTextClassNames(delta))}>
          {delta > -1 && '+'}
          {delta}
          {deltaFormat === 'percentage' && '%'} {deltaSuffix}
        </span>
      )}
    </div>
  );
}
